import { type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { colors } from '../../tokens/colors';
import { transitions, reducedMotionCSS } from '../../utils/motion';
import { Badge } from '../Badge/Badge';

/* ── Types ── */

type StatCardVariant = 'primary' | 'success' | 'warning' | 'error' | 'info';
type TrendDirection = 'up' | 'down' | 'neutral';

export interface StatCardProps {
  /** Valor principal (ex: "R$ 45.280,00") */
  value: string;
  /** Label descritiva (ex: "Receita total") */
  label: string;
  /** Ícone (elemento React, ex: <MaterialIcon name="payments" />) */
  icon: ReactNode;
  /** Variação de cor */
  variant?: StatCardVariant;
  /** Badge de tendência: texto + direção */
  trend?: { value: string; direction: TrendDirection };
  /** Callback ao clicar no card (torna o card interativo) */
  onClick?: () => void;
  className?: string;
}

/* ── Variant tokens ── */

const variantTokens: Record<StatCardVariant, {
  '--stat-icon-bg': string;
  '--stat-icon-color': string;
  '--stat-value-color': string;
  '--stat-label-color': string;
  '--stat-border-hover': string;
  '--stat-shadow-hover': string;
}> = {
  primary: {
    '--stat-icon-bg': `${colors.primary[500]}22`,
    '--stat-icon-color': colors.primary[500],
    '--stat-value-color': colors.primary[500],
    '--stat-label-color': colors.text.secondary,
    '--stat-border-hover': colors.primary[500],
    '--stat-shadow-hover': `0 0 20px ${colors.primary[300]}33`,
  },
  success: {
    '--stat-icon-bg': `${colors.success[500]}22`,
    '--stat-icon-color': colors.success[500],
    '--stat-value-color': colors.success[600],
    '--stat-label-color': colors.text.secondary,
    '--stat-border-hover': colors.success[500],
    '--stat-shadow-hover': `0 0 20px ${colors.success[300]}33`,
  },
  warning: {
    '--stat-icon-bg': `${colors.warning[500]}22`,
    '--stat-icon-color': colors.warning[500],
    '--stat-value-color': colors.warning[700],
    '--stat-label-color': colors.text.secondary,
    '--stat-border-hover': colors.warning[500],
    '--stat-shadow-hover': `0 0 20px ${colors.warning[300]}33`,
  },
  error: {
    '--stat-icon-bg': `${colors.error[500]}22`,
    '--stat-icon-color': colors.error[500],
    '--stat-value-color': colors.error[600],
    '--stat-label-color': colors.text.secondary,
    '--stat-border-hover': colors.error[500],
    '--stat-shadow-hover': `0 0 20px ${colors.error[300]}33`,
  },
  info: {
    '--stat-icon-bg': `${colors.info[500]}22`,
    '--stat-icon-color': colors.info[500],
    '--stat-value-color': colors.info[500],
    '--stat-label-color': colors.text.secondary,
    '--stat-border-hover': colors.info[500],
    '--stat-shadow-hover': `0 0 20px ${colors.info[300]}33`,
  },
};

/* ── Component ── */

export function StatCard({
  value,
  label,
  icon,
  variant = 'primary',
  trend,
  onClick,
  className,
}: StatCardProps): JSX.Element {
  const vToken = variantTokens[variant];
  const isClickable = !!onClick;

  return (
    <article
      className={cn(
        'ds-statcard',
        `ds-statcard--${variant}`,
        isClickable && 'ds-statcard--clickable',
        className,
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `${label}: ${value}` : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.xl,
        padding: '20px',
        background: colors.surface.card,
        cursor: isClickable ? 'pointer' : 'default',
        transition: `border-color ${transitions.normal}, box-shadow ${transitions.normal}, transform ${transitions.normal}`,
        ...vToken,
      }}
    >
      {/* Header: icon + trend */}
      <header
        className="ds-statcard__header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <span
          className="ds-statcard__icon"
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: borderRadius.xl,
            fontSize: '22px',
            background: 'var(--stat-icon-bg)',
            color: 'var(--stat-icon-color)',
          }}
        >
          {icon}
        </span>

        {trend && (
          <Badge
            variant={trend.direction === 'up' ? 'success' : trend.direction === 'down' ? 'error' : 'neutral'}
            size="sm"
            dot
          >
            <span style={{ fontWeight: 700 }}>{trend.value}</span>
          </Badge>
        )}
      </header>

      {/* Value */}
      <div
        className="ds-statcard__value"
        style={{
          fontSize: '28px',
          fontWeight: 800,
          color: 'var(--stat-value-color)',
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>

      {/* Label */}
      <div
        className="ds-statcard__label"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color: 'var(--stat-label-color)',
          lineHeight: 1.4,
        }}
      >
        {label}
      </div>

      <style>{`
        .ds-statcard:hover {
          border-color: var(--stat-border-hover) !important;
          box-shadow: var(--stat-shadow-hover), 0 4px 12px rgba(0,0,0,0.06) !important;
          transform: translateY(-1px);
        }
        .ds-statcard--clickable:active {
          transform: translateY(0);
        }
        .ds-statcard:focus-visible {
          outline: 2px solid var(--stat-border-hover);
          outline-offset: 2px;
        }
        ${reducedMotionCSS('.ds-statcard')}
      `}</style>
    </article>
  );
}

StatCard.displayName = 'StatCard';

/* ── Stats Grid (container) ── */

export interface StatsGridProps {
  children: ReactNode;
  columns?: number;
  minWidth?: string;
  className?: string;
}

export function StatsGrid({
  children,
  columns,
  minWidth = '240px',
  className,
}: StatsGridProps): JSX.Element {
  return (
    <div
      className={cn('ds-stats-grid', className)}
      style={{
        display: 'grid',
        gridTemplateColumns: columns
          ? `repeat(${columns}, 1fr)`
          : `repeat(auto-fill, minmax(${minWidth}, 1fr))`,
        gap: '16px',
      }}
    >
      {children}
    </div>
  );
}

StatsGrid.displayName = 'StatsGrid';
